// ============================================
// MEDFLOW - Supabase Auth Helpers
// ============================================

import { z } from 'zod';
import { supabase } from '@/lib/supabase';

const clinicSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  timezone: z.string().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
});

const membershipSchema = z.object({
  id: z.string(),
  clinicId: z.string(),
  role: z.string(),
  status: z.string(),
  permissions: z.record(z.unknown()).optional().default({}),
  clinic: clinicSchema,
});

const authContextSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string().nullable().optional(),
    name: z.string(),
    phone: z.string().nullable().optional(),
    defaultClinicId: z.string().nullable().optional(),
  }).nullable(),
  clinic: clinicSchema.nullable(),
  memberships: z.array(membershipSchema),
});

export type SupabaseAuthContext = z.infer<typeof authContextSchema>;

interface SignUpClinicOwnerInput {
  clinicName: string;
  slug: string;
  fullName: string;
  email: string;
  phone?: string;
  password: string;
}

function getSupabaseClient() {
  if (!supabase) {
    throw new Error('Supabase no está configurado todavía.');
  }

  return supabase;
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

export async function getSupabaseAuthContext(): Promise<SupabaseAuthContext> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('get_auth_context');

  if (error) {
    throw new Error(error.message || 'No se pudo cargar el contexto de autenticación.');
  }

  return authContextSchema.parse(data);
}

export async function signInWithSupabasePassword(email: string, password: string) {
  const client = getSupabaseClient();
  const { data, error } = await client.auth.signInWithPassword({ email, password });

  if (error) {
    throw new Error(error.message || 'No se pudo iniciar sesión.');
  }

  return data;
}

export async function signOutFromSupabase() {
  const client = getSupabaseClient();
  const { error } = await client.auth.signOut();

  if (error) {
    throw new Error(error.message || 'No se pudo cerrar la sesión.');
  }
}

export async function setSupabaseDefaultClinic(clinicId: string) {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('set_default_clinic', {
    p_clinic_id: clinicId,
  });

  if (error) {
    throw new Error(error.message || 'No se pudo cambiar la clínica activa.');
  }

  return authContextSchema.parse(data);
}

export async function updateSupabasePassword(password: string) {
  const client = getSupabaseClient();
  const { error } = await client.auth.updateUser({ password });

  if (error) {
    throw new Error(error.message || 'No se pudo cambiar la contraseña.');
  }
}

export async function bootstrapSupabaseClinic(input: Omit<SignUpClinicOwnerInput, 'email' | 'password' | 'fullName'>) {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('bootstrap_clinic', {
    p_name: input.clinicName,
    p_slug: input.slug,
    p_phone: input.phone,
    p_timezone: 'America/Santiago',
  });

  if (error) {
    throw new Error(error.message || 'No se pudo crear la clínica.');
  }

  return authContextSchema.parse(data);
}

export async function signUpClinicOwner(input: SignUpClinicOwnerInput) {
  const client = getSupabaseClient();

  const { data: signUpData, error: signUpError } = await client.auth.signUp({
    email: input.email,
    password: input.password,
    options: {
      data: {
        full_name: input.fullName,
        phone: input.phone ?? null,
      },
    },
  });

  if (signUpError) {
    throw new Error(getErrorMessage(signUpError, 'No se pudo crear la cuenta.'));
  }

  let session = signUpData.session;

  if (!session) {
    try {
      const signInResult = await signInWithSupabasePassword(input.email, input.password);
      session = signInResult.session;
    } catch {
      return {
        session: null,
        context: null,
        requiresEmailConfirmation: true,
      };
    }
  }

  try {
    const context = await bootstrapSupabaseClinic(input);

    return {
      session,
      context,
      requiresEmailConfirmation: false,
    };
  } catch (error) {
    try {
      await signOutFromSupabase();
    } catch {
      // Ignore cleanup errors
    }

    throw new Error(getErrorMessage(error, 'No se pudo completar el onboarding.'));
  }
}
